import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { faFireFlameCurved } from "@fortawesome/pro-solid-svg-icons/faFireFlameCurved";
import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import { Button } from "@mapmycustomers/ui";

import heatMapIcon from "@app/assets/map/legends/heatMap.svg";
import ButtonLink from "@app/component/ButtonLink";
import { MapLayer } from "@app/scene/map/store";
import { toggleLayerVisibility, updateHeatMapType } from "@app/scene/map/store/layers/actions";
import HeatMapLayerData from "@app/scene/map/store/layers/heatMap/HeatMapLayerData";
import { getHeatMapLayerData } from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";

import LegendCard, { BaseLegendCardProps } from "../LegendCard";

import FilteringInfo from "./component/FilteringInfo";
import HeatMapPanel from "./component/HeatMapPanel";
import Range from "./component/Range";
import styles from "./HeatMapLegendCard.module.scss";

interface Props extends BaseLegendCardProps {
  data: HeatMapLayerData;
  onUpdateHeatMapType: (payload: HeatMapLayerData["type"]) => void;
  toggleLayerVisibility: (payload: { name: MapLayer; visible: boolean }) => void;
}

const HeatMapLegendCard: React.FC<Props> = ({
  data,
  onUpdateHeatMapType,
  toggleLayerVisibility,
  ...props
}) => {
  const intl = useIntl();
  const analytics = useExtendedAnalytics("Heat map", useAnalytics());

  const [editPanelVisible, showEditPanel, hideEditPanel] = useBoolean();
  const handleVisibilityChange = useCallback(
    (visible) => {
      analytics.clicked([visible ? "On" : "Off"]);
      toggleLayerVisibility({ name: "heatMap", visible });
    },
    [analytics, toggleLayerVisibility]
  );

  useEffect(() => {
    if (editPanelVisible) {
      analytics.clicked(["Configure Heat Map"]);
    }
  }, [analytics, editPanelVisible]);

  const handleUpdateHeatMapType = useCallback(
    (type: HeatMapLayerData["type"]) => {
      onUpdateHeatMapType(type);
      analytics.clicked([
        "Configure Heat Map",
        type === EntityType.DEAL ? "Deal Amount" : "Activity",
      ]);
    },
    [analytics, onUpdateHeatMapType]
  );

  useChangeTracking(() => {
    if (props.active) {
      analytics.clicked([]);
    }
  }, [analytics, props.active]);

  return (
    <LegendCard
      {...props}
      icon={<FontAwesomeIcon icon={faFireFlameCurved} />}
      onVisibilityChange={handleVisibilityChange}
      title={intl.formatMessage({
        id: "map.layer.heatMap.legend",
        defaultMessage: "Heat Map",
        description: "Title of the Heat Map's legend",
      })}
    >
      {props.visible ? (
        <div className={styles.container}>
          <div className={styles.panel}>
            <FilteringInfo data={data} />
            <Range range={data.range} />
            {editPanelVisible && (
              <HeatMapPanel onHide={hideEditPanel} onUpdateHeatMapType={handleUpdateHeatMapType} />
            )}
          </div>
          <Button
            className={styles.editButton}
            leftIcon={<FontAwesomeIcon icon={faPen} />}
            onClick={showEditPanel}
            type="filled"
          >
            {intl.formatMessage({
              id: "map.layer.heatMap.legend.editButton",
              defaultMessage: "Configure Heat Map",
              description: "Map heatMap legend edit button",
            })}
          </Button>
        </div>
      ) : (
        <div className={styles.disabledLayerContainer}>
          <div className={styles.disabledLayer}>
            <img alt="Heat map icon" src={heatMapIcon} />
          </div>
          <div className={styles.enableLegendMessage}>
            {intl.formatMessage({
              id: "map.layer.heatMap.disabledState.text",
              defaultMessage: "Overlay a heat map to see hot spots for activities and deals",
              description: "Title of the Territories' legend disabled state",
            })}
          </div>
          <ButtonLink onClick={() => handleVisibilityChange(true)}>
            {intl.formatMessage({
              id: "map.layer.heatMap.enable.option",
              defaultMessage: "Enable Heat Map",
              description: "Option for users to enable heat map layer",
            })}
          </ButtonLink>
        </div>
      )}
    </LegendCard>
  );
};

const mapStateToProps = (state: RootState) => ({
  data: getHeatMapLayerData(state),
});

const mapDispatchToProps = {
  onUpdateHeatMapType: updateHeatMapType,
  toggleLayerVisibility,
};

export default connect(mapStateToProps, mapDispatchToProps)(HeatMapLegendCard);
